import React, { useEffect, useMemo, useRef, useState, useContext } from "react";

import Paper from "@mui/material/Paper";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import ButtonWithSpinner from "../../components/ButtonWithSpinner";
import StatTile from "../../components/Dashboard/StatTile";
import SegmentedBar from "../../components/Dashboard/SegmentedBar";
import { Box, Typography } from "@mui/material";
import { toast } from "../../services/toast";

import TextField from "@mui/material/TextField";
import { makeStyles } from 'tss-react/mui';
import { green, red, blue, purple, orange, amber, teal } from '@mui/material/colors';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import HistoryIcon from '@mui/icons-material/History';
import LabelImportantOutlinedIcon from '@mui/icons-material/LabelImportantOutlined';

import moment from 'moment';
import { isArray } from "lodash";
import { isEmpty } from "lodash";

import useActivityLog from "../../hooks/useActivityLog";
import SelectConection from "../../components/SelectConection";
import SelectUser from "../../components/SelectUser";
import { AuthContext } from "../../context/Auth/AuthContext";
import * as XLSX from 'xlsx';

import api from "../../services/api";

const REASON_COLORS = [blue[600], purple[600], green[600], orange[600], red[600], amber[700], teal[600]];
const MAX_REASON_SLICES = 7;

const useStyles = makeStyles()((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(2),
        overflowY: "auto",
        background: theme.palette.background.default,
    },
    filtersBar: {
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(2),
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 12,
    },
    cardsWrap: {
        marginBottom: theme.spacing(2),
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
        gap: theme.spacing(1.5),
    },
    distCard: {
        borderRadius: 12,
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
    },
    distTitle: {
        fontSize: "0.68rem",
        textTransform: "uppercase",
        letterSpacing: 0.8,
        marginBottom: 10,
        color: theme.palette.text.secondary,
    },
    distNote: {
        fontSize: "0.68rem",
        color: theme.palette.text.disabled,
        marginTop: 8,
    },
    tablePaper: {
        borderRadius: 12,
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
    },
    tableHeader: {
        background: theme.palette.primary.main,
        "& th": {
            color: theme.palette.primary.contrastText,
            fontWeight: 700,
            fontSize: "0.72rem",
            padding: "8px 10px",
            whiteSpace: "nowrap",
        },
    },
    td: {
        padding: "6px 10px",
        fontSize: "0.8rem",
    },
    tableTitle: {
        fontWeight: 700,
        fontSize: "0.9rem",
        padding: theme.spacing(1.5, 2),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    pointer: {
        cursor: "pointer"
    },
    faint: {
        color: theme.palette.text.disabled,
    },
}));

const ActivityLog = () => {

    const { classes } = useStyles();
    const [data, setData] = useState([]);
    const [dateFrom, setDateFrom] = useState(moment().format('YYYY-MM-DD'));
    const [dateTo, setDateTo] = useState(moment().format('YYYY-MM-DD'));
    const [hasMore, setHasMore] = useState(true);
    const tableContainerRef = useRef();

    const [loading, setLoading] = useState(false);
    const { user } = useContext(AuthContext);
    const { profile } = user;

    const [selectedUser, setSelectedUser] = useState(null);
    const [name, setName] = useState("");
    const [pageNumber, setPageNumber] = useState(1);
    const [total, setTotal] = useState(0);

    const [whatsappId, setWhatsappId] = useState(null);
    const companyId = localStorage.getItem("companyId");

    const { find } = useActivityLog();

    useEffect(() => {
        if (profile !== "admin") {
            setSelectedUser(user.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setPageNumber(1);
        fetchData(1, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, whatsappId, selectedUser, dateFrom, dateTo]);

    async function fetchData(page, replace) {
        setLoading(true);

        let params = {
            pageNumber: page
        };

        if (selectedUser) {
            params = {
                ...params,
                userId: selectedUser
            };
        }
        if (whatsappId) {
            params = {
                ...params,
                whatsappId: whatsappId,
            };
        }
        if (name) {
            params = {
                ...params,
                searchParam: name,
            };
        }

        if (!isEmpty(dateFrom) && moment(dateFrom).isValid()) {
            params = {
                ...params,
                date_from: moment(dateFrom).format("YYYY-MM-DD"),
            };
        }
        if (!isEmpty(dateTo) && moment(dateTo).isValid()) {
            params = {
                ...params,
                date_to: moment(dateTo).format("YYYY-MM-DD"),
            };
        }

        const response = await find(params);

        if (isArray(response.activityLog)) {
            setData(prevData => replace ? response.activityLog : [...prevData, ...response.activityLog]);
            setTotal(response.count);
            setHasMore(response.hasMore);
        } else {
            setData([]);
            setTotal(0);
            setHasMore(false);
        }

        setLoading(false);
    }

    const handleScroll = (event) => {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        if (scrollTop + clientHeight >= scrollHeight - 5 && hasMore && !loading) {
            const nextPage = pageNumber + 1;
            setPageNumber(nextPage);
            fetchData(nextPage, false);
        }
    };

    useEffect(() => {
        const el = tableContainerRef.current;
        if (!el) return;
        el.addEventListener("scroll", handleScroll);
        return () => el.removeEventListener("scroll", handleScroll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasMore, loading, pageNumber]);

    const motivoBreakdown = useMemo(() => {
        const counts = {};
        data.forEach((item) => {
            const key = (item.reason || "").trim() || "Sin motivo";
            counts[key] = (counts[key] || 0) + 1;
        });

        const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
        const top = entries.slice(0, MAX_REASON_SLICES);
        const resto = entries.slice(MAX_REASON_SLICES).reduce((sum, [, v]) => sum + v, 0);
        if (resto > 0) top.push(["Otros", resto]);

        return top.map(([name, value], i) => ({ name, value, color: REASON_COLORS[i % REASON_COLORS.length] }));
    }, [data]);

    const topReason = motivoBreakdown.find((m) => m.name !== "Otros");

    function renderList() {
        return data.map((item, index) => {
            const { ticket, whatsapp, reason, comment, user, createdAt } = item;
            const contactName = ticket?.contact?.name ?? '';
            const whatsappName = whatsapp?.name ?? '';
            const formattedDate = moment(createdAt).format('MM/DD/YYYY HH:mm');

            return (
                <TableRow key={index} hover>
                    <TableCell className={classes.td}>
                        <a
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none', fontWeight: 700 }}
                            href={`tickets/${ticket?.uuid ?? ''}`}
                        >
                            {contactName || <span className={classes.faint}>—</span>}
                        </a>
                    </TableCell>
                    <TableCell align="center" className={classes.td}>
                        {whatsappName || <span className={classes.faint}>—</span>}
                    </TableCell>
                    <TableCell align="center" className={classes.td}>
                        {reason || <span className={classes.faint}>Sin motivo</span>}
                    </TableCell>
                    <TableCell className={classes.td}>
                        {comment || <span className={classes.faint}>—</span>}
                    </TableCell>
                    <TableCell align="center" className={classes.td}>
                        {user?.name ?? <span className={classes.faint}>—</span>}
                    </TableCell>
                    <TableCell align="center" className={classes.td}>
                        {formattedDate}
                    </TableCell>
                </TableRow>
            )
        });
    }

    const exportToExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(data.map(item => ({
            Nombre: item.ticket?.contact?.name ?? '',
            Conexion: item.whatsapp?.name ?? '',
            Motivo: item.reason ?? '',
            Observacion: item.comment ?? '',
            Usuario: item.user?.name ?? '',
            Fecha: moment(item.createdAt).format('MM/DD/YYYY HH:mm'),
        })));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Registros");
        XLSX.writeFile(workbook, `Listado_Detallado_${moment().format('MMDDYYYY')}.xlsx`);
    };

    const downloadPDF = async () => {
        try {

            const response = await api.request({
                url: `/generate-pdf`,
                method: 'POST',
                data: data,
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Reporte_Actividad_${moment().format('MMDDYYYY')}.pdf`);
            document.body.appendChild(link);
            link.click();
        } catch (error) {
            console.error('Error al generar el PDF:', error);
            toast.error('Error al generar el PDF');
        }
    };

    return (
        <MainContainer maxWidth={false}>
            <MainHeader>
                <Title>Historial de cierres</Title>
            </MainHeader>
            <Paper className={classes.mainPaper} elevation={0}>

                <div className={classes.filtersBar}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
                        <TextField
                            label="Nombre del Cliente"
                            type="text"
                            size="small"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                        <Box sx={{ minWidth: 180 }}>
                            <SelectConection
                                value={whatsappId}
                                onChange={values => setWhatsappId(values)}
                            />
                        </Box>
                        <Box sx={{ minWidth: 160 }}>
                            <SelectUser
                                selectUserid={selectedUser}
                                companyId={companyId}
                                onChange={(values) => setSelectedUser(values)}
                                disabled={profile === "user"}
                            />
                        </Box>
                        <TextField
                            label="Desde"
                            type="date"
                            size="small"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <TextField
                            label="Hasta"
                            type="date"
                            size="small"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <ButtonWithSpinner
                            loading={loading}
                            onClick={() => fetchData(1, true)}
                            variant="contained"
                            color="primary"
                            size="small"
                        >
                            Filtrar
                        </ButtonWithSpinner>
                        <ButtonWithSpinner
                            onClick={downloadPDF}
                            variant="outlined"
                            color="secondary"
                            size="small"
                        >
                            Generar Reporte
                        </ButtonWithSpinner>
                        <ButtonWithSpinner
                            onClick={exportToExcel}
                            variant="outlined"
                            color="secondary"
                            size="small"
                        >
                            Exportar a Excel
                        </ButtonWithSpinner>
                    </Box>
                </div>

                {}
                <Box className={classes.cardsWrap}>
                    <StatTile
                        label="Cierres en el período"
                        value={total}
                        icon={<HistoryIcon />}
                        accent={blue[600]}
                    />
                    <StatTile
                        label="Motivo más frecuente"
                        value={topReason?.name ?? "—"}
                        sub={topReason ? `${topReason.value} cierres` : ""}
                        icon={<LabelImportantOutlinedIcon />}
                        accent={purple[600]}
                    />
                </Box>

                {}
                {motivoBreakdown.length > 0 && (
                    <Box className={classes.distCard}>
                        <Typography className={classes.distTitle}>
                            Motivos de cierre
                        </Typography>
                        <SegmentedBar items={motivoBreakdown} />
                        {hasMore && (
                            <Typography className={classes.distNote}>
                                Basado en los {data.length} de {total} registros cargados hasta ahora — seguí bajando en la tabla para completar el total.
                            </Typography>
                        )}
                    </Box>
                )}

                {}
                <Paper className={classes.tablePaper} elevation={0}>
                    <Typography className={classes.tableTitle}>
                        Registros de actividades: {total}
                    </Typography>
                    <TableContainer ref={tableContainerRef} style={{ maxHeight: 500 }}>
                        <Table size="small" stickyHeader>
                            <TableHead className={classes.tableHeader}>
                                <TableRow>
                                    <TableCell>Nombre</TableCell>
                                    <TableCell align="center">Conexión</TableCell>
                                    <TableCell align="center">Motivo</TableCell>
                                    <TableCell>Observación</TableCell>
                                    <TableCell align="center">Usuario</TableCell>
                                    <TableCell align="center">Fecha</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" className={`${classes.td} ${classes.faint}`} style={{ padding: 24 }}>
                                            Sin registros en el período
                                        </TableCell>
                                    </TableRow>
                                ) : renderList()}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Paper>
        </MainContainer>
    );
};

export default ActivityLog;
